/**
 * zing-integration.js
 *
 * Handles the Zing Cover opt-in checkbox on the product detail page.
 *
 * Responsibilities:
 *   1. When the checkbox is checked, inject hidden line item properties
 *      into the product's add-to-cart form so Shopify records the opt-in.
 *   2. When the checkbox is unchecked, remove those properties.
 *   3. When the selected variant changes price, show or hide the checkbox
 *      based on ZNG eligibility ranges.
 *
 * No ZNG credentials or API calls are made here — all server communication
 * happens in the webhook server after payment is confirmed.
 */

(function () {
  'use strict';

  var PROP_OPT_IN = 'properties[_zing_quote_requested]';
  var PROP_CATEGORY = 'properties[_zing_category]';

  function init() {
    document.querySelectorAll('.zing-insurance').forEach(function (container) {
      setupContainer(container);
    });
  }

  function setupContainer(container) {
    var checkbox = container.querySelector('.zing-insurance__checkbox');
    if (!checkbox) return;

    var priceMin = parseInt(container.dataset.priceMin, 10);
    var priceMax = parseInt(container.dataset.priceMax, 10);
    var zingCategory = container.dataset.zingCategory;

    var productForm = findProductForm(container);
    if (!productForm) {
      console.warn('[Zing] Could not find product form. Opt-in will not be recorded.');
      return;
    }

    // Sync hidden inputs whenever the checkbox changes
    checkbox.addEventListener('change', function () {
      syncHiddenInputs(productForm, checkbox.checked, zingCategory);
    });

    // Show/hide based on variant price changes
    listenForVariantChanges(container, checkbox, productForm, priceMin, priceMax, zingCategory);
  }

  /**
   * Adds or removes the two hidden inputs that record the Zing opt-in as
   * Shopify line item properties.
   */
  function syncHiddenInputs(form, isChecked, category) {
    // Remove stale inputs first
    [PROP_OPT_IN, PROP_CATEGORY].forEach(function (name) {
      var existing = form.querySelector('[name="' + name + '"]');
      if (existing) existing.remove();
    });

    if (isChecked) {
      addHiddenInput(form, PROP_OPT_IN, 'true');
      addHiddenInput(form, PROP_CATEGORY, category);
    }
  }

  function addHiddenInput(form, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  /**
   * Walks up the DOM to find the product's add-to-cart form.
   * Tries the parent chain first, then falls back to a page-level query.
   */
  function findProductForm(container) {
    // Walk up to the nearest enclosing form
    var el = container.parentElement;
    while (el) {
      if (el.tagName === 'FORM' && el.action && el.action.indexOf('/cart/add') !== -1) {
        return el;
      }
      // Dawn-style product-form custom element wraps a <form>
      if (el.tagName === 'PRODUCT-FORM') {
        var inner = el.querySelector('form');
        if (inner) return inner;
      }
      el = el.parentElement;
    }

    // Fallback: first add-to-cart form on the page
    return document.querySelector('form[action*="/cart/add"]');
  }

  /**
   * Listens for variant price changes and toggles the zing container
   * visibility accordingly.
   *
   * Handles:
   *   - Prestige theme's custom `variant:change` event
   *   - Generic change events on variant selector inputs ([name="id"])
   */
  function listenForVariantChanges(container, checkbox, productForm, priceMin, priceMax, category) {
    // Prestige theme broadcasts this event on the form element with bubbles: true
    var section = container.closest('.shopify-section') || document;

    section.addEventListener('variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      if (variant.available === false) {
        container.style.display = 'none';
        checkbox.checked = false;
        syncHiddenInputs(productForm, false, category);
        return;
      }
      if (typeof variant.price === 'number') {
        handlePriceChange(container, checkbox, productForm, variant.price, priceMin, priceMax, category);
      }
    });

    // Fallback for themes using a native <select name="id"> variant picker
    section.addEventListener('change', function (event) {
      var target = event.target;
      if (target.name !== 'id') return;

      // Some themes expose price on the <option> data attribute
      var selectedOption = target.options && target.options[target.selectedIndex];
      if (selectedOption && selectedOption.dataset.price) {
        handlePriceChange(
          container,
          checkbox,
          productForm,
          parseInt(selectedOption.dataset.price, 10),
          priceMin,
          priceMax,
          category
        );
      }
    });
  }

  function handlePriceChange(container, checkbox, productForm, price, priceMin, priceMax, category) {
    var isEligible = price >= priceMin && price <= priceMax;
    container.style.display = isEligible ? '' : 'none';

    if (!isEligible && checkbox.checked) {
      checkbox.checked = false;
      syncHiddenInputs(productForm, false, category);
    }
  }

  // Run after the DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
